import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SuiteApiError } from "@weco/suite-client";
import { videoService } from "../api/video.api";
import type { DirectCallStatus } from "../api/types/video";

export interface OutgoingRing {
  inviteId: string;
  sub: string;
  name: string;
  status: DirectCallStatus;
}

export interface RingStatusPill {
  kind: "ringing" | "accepted";
  name: string;
}

const POLL_MS = 2000;
const ACCEPTED_PILL_HOLD_MS = 2500;

/**
 * Tracks colleagues rung into an in-progress call from the People panel.
 *
 * Invites are created by POST /direct-calls/ring/; the serializer does not
 * echo `callee_sub`, so we keep name/sub locally from the person who was
 * picked. Status is then polled via GET /direct-calls/{id}/ (caller can
 * retrieve). Once the callee's LiveKit identity (their `sub`) shows up in
 * `joinedSubs`, they leave the "invited · not joined" list.
 */
export function useOutgoingRings(roomId: string, joinedSubs: ReadonlySet<string>) {
  const { t } = useTranslation();
  const [invited, setInvited] = useState<OutgoingRing[]>([]);
  const invitedRef = useRef(invited);
  invitedRef.current = invited;
  const [pill, setPill] = useState<RingStatusPill | null>(null);
  const [ringingSub, setRingingSub] = useState<string | null>(null);
  const holdTimerRef = useRef<number | null>(null);

  const clearHold = () => {
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const showPill = useCallback((next: RingStatusPill | null, holdMs?: number) => {
    clearHold();
    setPill(next);
    if (next && holdMs) {
      holdTimerRef.current = window.setTimeout(() => {
        setPill(null);
        holdTimerRef.current = null;
      }, holdMs);
    }
  }, []);

  useEffect(() => () => clearHold(), []);

  // Drop anyone who has actually joined the LiveKit room.
  useEffect(() => {
    if (joinedSubs.size === 0) return;
    setInvited((prev) => prev.filter((row) => !joinedSubs.has(row.sub)));
  }, [joinedSubs]);

  // Poll still-ringing invites so the pill can flip to "accepted — joining".
  const ringingIds = invited
    .filter((row) => row.status === "ringing")
    .map((row) => row.inviteId)
    .join(",");

  useEffect(() => {
    if (!ringingIds) return;

    let cancelled = false;
    const tick = async () => {
      const ids = ringingIds.split(",");
      const statuses = await Promise.all(
        ids.map(async (id) => {
          try {
            const invite = await videoService.getDirectCall(id);
            return { id, status: invite.status };
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;

      for (const update of statuses) {
        if (!update) continue;
        const row = invitedRef.current.find((p) => p.inviteId === update.id);
        if (!row) continue;
        if (update.status === "accepted") {
          if (row.status !== "accepted") {
            setInvited((prev) =>
              prev.map((p) => (p.inviteId === update.id ? { ...p, status: "accepted" } : p)),
            );
            showPill({ kind: "accepted", name: row.name });
          }
        } else if (
          update.status === "declined" ||
          update.status === "missed" ||
          update.status === "canceled"
        ) {
          setInvited((prev) => prev.filter((p) => p.inviteId !== update.id));
          setPill((current) => (current?.name === row.name ? null : current));
        }
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [ringingIds, showPill]);

  const ringColleague = useCallback(
    async (user: { sub: string; name: string }) => {
      if (joinedSubs.has(user.sub)) return;
      if (invited.some((row) => row.sub === user.sub && row.status === "ringing")) return;

      setRingingSub(user.sub);
      showPill({ kind: "ringing", name: user.name });
      try {
        const result = await videoService.ringIntoRoom(roomId, [user.sub]);
        const invite = result.invites[0];
        if (!invite) {
          toast.error(t("call.ringIn.failed"));
          setPill(null);
          return;
        }
        setInvited((prev) => [
          ...prev.filter((row) => row.sub !== user.sub),
          { inviteId: invite.id, sub: user.sub, name: user.name, status: "ringing" },
        ]);
      } catch (err) {
        setPill(null);
        const message =
          err instanceof SuiteApiError ? err.message : t("call.ringIn.failed");
        toast.error(message);
      } finally {
        setRingingSub(null);
      }
    },
    [invited, joinedSubs, roomId, showPill, t],
  );

  // If they join while the accepted pill is showing, keep it up briefly so
  // the "accepted — joining" copy is actually readable, then clear it.
  const heldAcceptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (pill?.kind !== "accepted") return;
    const matching = invited.find((row) => row.name === pill.name);
    if (!matching || !joinedSubs.has(matching.sub)) return;
    if (heldAcceptedRef.current === matching.inviteId) return;
    heldAcceptedRef.current = matching.inviteId;
    showPill(pill, ACCEPTED_PILL_HOLD_MS);
  }, [invited, joinedSubs, pill, showPill]);

  const invitedNotJoined = useMemo(
    () => invited.filter((row) => !joinedSubs.has(row.sub) && row.status !== "declined" && row.status !== "canceled" && row.status !== "missed"),
    [invited, joinedSubs],
  );

  return { invited: invitedNotJoined, pill, ringingSub, ringColleague };
}
